import {format, isSameYear, isValid} from 'date-fns'
import ptBR from 'date-fns/locale/pt-BR'
import {differenceInDays} from '@/utils/helpers/DateHelper'

export function dateFilter(value:string|number|Date):string|undefined {
    return formatter({
        value,
        time: false
    })
}

export function dateTimeFilter(value:string|number|Date):string|undefined {
    return formatter({
        value,
        time: true,
    })
}

export function timeFilter(value:DateLike):string|undefined {
    const date = toDate(value)

    if (!date) {
        return undefined
    }

    return format(date, 'HH:mm')
}

// "21 de setembro", acrescentando o ano apenas quando a data é de outro ano.
export function dayMonthFilter(value:DateLike):string|undefined {
    const date = toDate(value)

    if (!date) {
        return undefined
    }

    return dayMonth(date)
}

export function relativeDayFilter(value:DateLike):string {
    const date = toDate(value)

    if (!date) {
        return SEM_DATA_LABEL
    }

    const daysAgo = differenceInDays(new Date(), date)

    if (daysAgo === 0) {
        return capitalize(`hoje, ${format(date, "d 'de' MMMM", {locale: ptBR})}`)
    }

    if (daysAgo === 1) {
        return capitalize(`ontem, ${format(date, "d 'de' MMMM", {locale: ptBR})}`)
    }

    if (daysAgo > 1 && daysAgo < 7) {
        return capitalize(format(date, "EEEE, d 'de' MMMM", {locale: ptBR}))
    }

    return capitalize(dayMonth(date))
}

export function dayKey(value:DateLike):string {
    const date = toDate(value)

    if (!date) {
        return SEM_DATA_KEY
    }

    return format(date, 'yyyy-MM-dd')
}

export function isToday(value:DateLike):boolean {
    const date = toDate(value)

    return !!date && differenceInDays(new Date(), date) === 0
}

export const SEM_DATA_KEY = 'sem-data'
export const SEM_DATA_LABEL = 'Sem data'

// Os campos de data dos models são opcionais, e estas funções já tratam a ausência
// em runtime - a assinatura reflete isso em vez de exigir cast na chamada.
export type DateLike = string|number|Date|undefined

function toDate(value:DateLike):Date|undefined {
    if (value instanceof Date) {
        return isValid(value) ? value : undefined
    }

    if ((typeof value === 'string' && !!value.length) || typeof value === 'number') {
        const date = new Date(value)

        return isValid(date) ? date : undefined
    }

    return undefined
}

function dayMonth(date:Date):string {
    if (isSameYear(date, new Date())) {
        return format(date, "d 'de' MMMM", {locale: ptBR})
    }

    return format(date, "d 'de' MMMM 'de' yyyy", {locale: ptBR})
}

function capitalize(value:string):string {
    return value.charAt(0).toUpperCase() + value.slice(1)
}

function formatter(cfg:IConfigDateFormat):string|undefined {
    const valueIsDate = cfg.value instanceof Date
    const valueNeedsDateWrapper = (typeof cfg.value === "string" && !!cfg.value.length)|| 
                             typeof cfg.value === "number"

    if (valueIsDate) {
        if (cfg.time) {
            return format(cfg.value as Date, 'dd/MM/yyyy HH:mm:ss');
        }

        return format(cfg.value as Date, 'dd/MM/yyyy');
    }

    if (valueNeedsDateWrapper) {
        if (cfg.time) {
            return format(new Date(cfg.value), 'dd/MM/yyyy HH:mm:ss');
        }

        return format(new Date(cfg.value), 'dd/MM/yyyy');
    }

    return undefined;   
}

interface IConfigDateFormat {
    value:string|number|Date,
    time:boolean,
}
